import React, { useEffect, useRef, useState } from "react";
import GridContainer from "common/components/Grid/GridContainer.jsx";
import GridItem from "common/components/Grid/GridItem.jsx";
import Card from "common/components/Card/Card.jsx";
import CardHeader from "common/components/Card/CardHeader.jsx";
import CardIcon from "common/components/Card/CardIcon.jsx";
import CardBody from "common/components/Card/CardBody.jsx";

import BandwidthStreaming from "./TrafficStreaming";
import TrafficByDeviceContainer from "./TrafficByDeviceContainer";
import BandwidthGraph from "./BandwidthGraph";
import { isNativeIosPlatform, isWebDevice } from "../../deviceInfo";
import { useSelector } from "react-redux";
import * as api from "core-module/services/api";
import TimelineIcon from "@material-ui/icons/Timeline";

import { makeStyles } from "@material-ui/core/styles";
import { cardTitle } from "assets/jss/material-dashboard-pro-react.jsx";

import { useInterfaceStats } from "./interfaceHooks"; // <-- adjust path

const useStyles = makeStyles(() => ({
  cardTitle,
}));

const EXTRA_BOTTOM_PX = 24;

// Measure an element’s rendered size (handles sidebar open/close, window resize, etc.)
function useSize(ref) {
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
}

const TrafficDashboard = () => {
  const classes = useStyles();
  const userid = useSelector((state) => state.services.ids.user);

  const [cardHeight, setCardHeight] = useState();
  const [macAddresses, setMacAddresses] = useState([]);
  const [labels, setLabels] = useState([]);
  const [hasRouter, setHasRouter] = useState(false);

  // This box is what we measure; the graph fills it.
  const graphBoxRef = useRef(null);
  const { width: graphWidth, height: graphHeight } = useSize(graphBoxRef);
  const useNativeIosLayout = isNativeIosPlatform();
  const graphControlsHeight = useNativeIosLayout
    ? graphWidth < 400
      ? 80
      : graphWidth < 700
      ? 56
      : 32
    : EXTRA_BOTTOM_PX;

  function getLabel(device) {
    if (device.alias !== "") return device.alias;

    if (device.vendor === "unknown" || device.vendor === "") {
      return "MAC:" + "..." + device.mac.slice(5);
    }
    return device.vendor;
  }

  useEffect(() => {
    api.get(`user.${userid}/wifiDeviceInfo`).then((res) => {
      const myLabels = res?.list?.map((device) => getLabel(device)) || [];
      myLabels.splice(0, 0, "All Devices");
      setLabels(myLabels);

      const myMacAddresses = res?.list?.map((device) => device.mac) || [];
      setMacAddresses(myMacAddresses);

      setHasRouter(!!res?.hasRouter);
    });

    const getCardHeight = async () => {
      const isWebResponse = await isWebDevice();
      setCardHeight(isWebResponse ? "60vh" : "40vh");
    };
    getCardHeight();
  }, [userid]);

  const [statsWsError, stats] = useInterfaceStats(
    `user.${userid}/broadband_stats_token`
  );

  // Adjust this if your CardHeader is taller/shorter
  const HEADER_ESTIMATE_PX = 90;

  return (
    <GridContainer justify="center">
      <GridItem xs={12} md={12} lg={12}>
        <Card style={{ height: cardHeight, backgroundColor: "#43464b" }}>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <TimelineIcon />
            </CardIcon>
            <h4 className={classes.cardTitle}>Live Bandwidth</h4>
          </CardHeader>

          <CardBody
            style={{
              // Make CardBody a real sizing container
              height: `calc(100% - ${HEADER_ESTIMATE_PX}px)`,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              ref={graphBoxRef}
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                paddingBottom: useNativeIosLayout ? 0 : EXTRA_BOTTOM_PX,
                boxSizing: "border-box",
              }}
            >
              {stats?.downStreamData?.length > 0 ? (
                <BandwidthGraph
                  downStreamData={stats.downStreamData}
                  upStreamData={stats.upStreamData}
                  downStreamPackets={stats.downStreamPackets}
                  upStreamPackets={stats.upStreamPackets}
                  width={graphWidth}
                  height={Math.max(0, graphHeight - graphControlsHeight)}
                  showLegend={true}
                />
              ) : (
                <div style={{ padding: 20, textAlign: "center" }}>
                  Loading bandwidth data...
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </GridItem>
      {(macAddresses || []).length > 0 && (
        <GridItem xs={12} md={12} lg={12}>
          <TrafficByDeviceContainer
            labels={labels}
            macAddresses={macAddresses}
            cardHeight={cardHeight}
            userId={userid}
          />
        </GridItem>
      )}
    </GridContainer>
  );
};

export default TrafficDashboard;
